import { useState } from "react"
import { useAuth0 } from "ct-auth"
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { addGroupParticipants, addSessionPlayer } from "../../../data-utils/data"
import logger from "../../../Logger/index.js"

const pageLogger = logger.child({ module: "AddUsersDialog" })

// Mirrors the server-side cap in /addGroupParticipants
// (validation.max_group_participants_per_add, default 100). Also bounds the
// per-row session-mode loop so one submit can't fan out into hundreds of POSTs.
const MAX_PARTICIPANTS = 100

const emailLooksValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim())
const normalizeEmail = (email) =>
  String(email || "")
    .trim()
    .toLowerCase()
const emptyRow = () => ({ email: "", name: "", role: "" })

// Dialog to add user(s) to an existing exercise.
// - mode "group": add one or more participants (each gets their own session),
//   optionally scheduled to start at a chosen date/time.
// - mode "session": add one or more players (each assigned a free-text role) to a
//   collaborative multi-role session.
const AddUsersDialog = ({ open, onClose, mode = "group", targetName, org = null, onAdded }) => {
  const { getAccessTokenSilently, user } = useAuth0()
  const isGroup = mode === "group"
  const [rows, setRows] = useState([emptyRow()])
  const [scheduleAt, setScheduleAt] = useState("") // datetime-local string; blank = start now
  const [inFlight, setInFlight] = useState(false)
  const [error, setError] = useState(null)
  // Players added whose invite email couldn't be sent: they ARE in the exercise (not an error).
  const [notice, setNotice] = useState(null)

  const reset = () => {
    setRows([emptyRow()])
    setScheduleAt("")
    setError(null)
    setNotice(null)
    setInFlight(false)
  }

  const handleClose = () => {
    if (inFlight) return
    reset()
    onClose?.()
  }

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)))
  }
  const addRow = () => setRows((prev) => [...prev, emptyRow()])
  const removeRow = (idx) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)))

  // A row is complete with a valid email AND a name — plus a role in session
  // (multi-role) mode, where each player must be assigned a role.
  const rowComplete = (r) => emailLooksValid(r.email) && r.name.trim().length > 0 && (isGroup || r.role.trim().length > 0)
  const rowTouched = (r) => r.email.trim() || r.name.trim() || r.role.trim()
  const validRows = rows.filter(rowComplete)
  // Emails entered more than once in this batch (case-insensitive) — each would be
  // POSTed twice, so block submit. Mirrors the server's normalized dedupe.
  const dupEmails = (() => {
    const seen = new Set()
    const dups = new Set()
    for (const r of rows) {
      const e = normalizeEmail(r.email)
      if (!e) continue
      if (seen.has(e)) dups.add(e)
      else seen.add(e)
    }
    return dups
  })()
  const isDup = (r) => dupEmails.has(normalizeEmail(r.email))
  const tooMany = validRows.length > MAX_PARTICIPANTS
  const hasInvalid = rows.some((r) => rowTouched(r) && !rowComplete(r)) || dupEmails.size > 0
  const scheduleInPast = !!scheduleAt && new Date(scheduleAt).getTime() < Date.now()
  const canSubmit = !inFlight && validRows.length > 0 && !hasInvalid && !tooMany && !scheduleInPast

  const handleSubmit = async () => {
    setError(null)
    setNotice(null)
    setInFlight(true)
    try {
      const accessToken = await getAccessTokenSilently()
      if (isGroup) {
        // A typed role is that person's own (matched to the scenario by the server); blank plays the
        // group's scope, or the whole incident.
        const participants = validRows.map((r) => ({
          email: normalizeEmail(r.email),
          name: r.name.trim() || normalizeEmail(r.email),
          ...(r.role.trim() ? { soloRoles: [r.role.trim()] } : {}),
        }))
        const scheduledTime = scheduleAt ? new Date(scheduleAt).getTime() : undefined
        await addGroupParticipants(accessToken, user?.sub, { groupName: targetName, participants, org, scheduledTime })
      } else {
        // The session endpoint takes one player per call, so add each row sequentially
        // and track failures so a bad row doesn't hide the players that did get added.
        const players = validRows.map((r) => ({
          email: normalizeEmail(r.email),
          name: r.name.trim() || normalizeEmail(r.email),
          role: r.role.trim() || undefined,
        }))
        const failedEmails = []
        const notInvited = []
        for (const player of players) {
          try {
            const result = await addSessionPlayer(accessToken, user?.sub, { session: targetName, player, org })
            if (result?.inviteError) notInvited.push(result.inviteError)
          } catch (err) {
            pageLogger.error({ err, code: err.serverCode, status: err.status, email: player.email, targetName }, "Failed to add one session player")
            failedEmails.push(player.email)
          }
        }
        if (failedEmails.length > 0) {
          const allFailed = failedEmails.length === players.length
          if (allFailed) {
            setError("Unable to add players. Try again.")
          } else {
            onAdded?.()
            // Keep only the failed rows so a retry doesn't re-POST players that already got added.
            // Use a functional update so we filter the latest rows, not the snapshot captured at
            // submit time (inputs are disabled during submit, but this keeps the two in lockstep).
            const failedSet = new Set(failedEmails)
            setRows((prev) => prev.filter((r) => failedSet.has(normalizeEmail(r.email))))
            const shownFailed =
              failedEmails.length > 5 ? `${failedEmails.slice(0, 5).join(", ")} and ${failedEmails.length - 5} more` : failedEmails.join(", ")
            setError(`Added ${players.length - failedEmails.length}. Couldn't add: ${shownFailed}.`)
            // Some of the added people may not have been invited — say so too (review of #691).
            if (notInvited.length > 0) setNotice(notInvited.join(" "))
          }
          setInFlight(false)
          return
        }
        if (notInvited.length > 0) {
          // Everyone was added; some invites didn't go out. Say so and keep the dialog open.
          onAdded?.()
          setRows([emptyRow()])
          setNotice(notInvited.join(" "))
          setInFlight(false)
          return
        }
      }
      pageLogger.info({ mode, targetName, count: validRows.length, scheduled: !!scheduleAt }, "Added users to exercise")
      reset()
      onAdded?.()
      onClose?.()
    } catch (err) {
      pageLogger.error({ err, code: err.serverCode, status: err.status, mode, targetName }, "Error adding users to exercise")
      setError(messageForError(err))
      setInFlight(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isGroup ? "Add Participants" : "Add Players"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {isGroup
            ? "New participants each get their own session and are invited automatically. People already in the exercise are skipped."
            : "Add one or more players to this collaborative session and assign each a role. They'll be invited automatically."}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="add-users-notice">
            {notice}
          </Alert>
        )}
        {tooMany && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Too many at once — add at most {MAX_PARTICIPANTS} {isGroup ? "participants" : "players"} per submission.
          </Alert>
        )}
        <Stack spacing={2}>
          {rows.map((row, idx) => (
            <Stack key={idx} direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="Name"
                size="small"
                fullWidth
                required
                disabled={inFlight}
                value={row.name}
                error={!!row.email.trim() && !row.name.trim()}
                onChange={(e) => updateRow(idx, "name", e.target.value)}
              />
              <TextField
                label="Email"
                size="small"
                fullWidth
                required
                disabled={inFlight}
                value={row.email}
                error={!!row.email.trim() && (!emailLooksValid(row.email) || isDup(row))}
                helperText={isDup(row) ? "Duplicate email" : undefined}
                onChange={(e) => updateRow(idx, "email", e.target.value)}
              />
              <TextField
                label={isGroup ? "Role (optional)" : "Role"}
                size="small"
                fullWidth
                required={!isGroup}
                disabled={inFlight}
                value={row.role}
                error={!isGroup && !!row.email.trim() && !row.role.trim()}
                onChange={(e) => updateRow(idx, "role", e.target.value)}
              />
              {rows.length > 1 && (
                <IconButton aria-label="remove" onClick={() => removeRow(idx)} disabled={inFlight} sx={{ mt: 0.5 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
        <Button startIcon={<AddIcon />} onClick={addRow} disabled={inFlight} sx={{ mt: 1 }}>
          Add another
        </Button>
        {isGroup && (
          <TextField
            label="Schedule start (optional)"
            type="datetime-local"
            size="small"
            disabled={inFlight}
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            error={scheduleInPast}
            helperText={scheduleInPast ? "Pick a time in the future." : "Leave blank to start (and invite) now."}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 3, minWidth: 280 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={inFlight}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {inFlight ? "Adding…" : isGroup ? "Add Participants" : "Add Players"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const messageForError = (err) => {
  if (err?.status === 409) return "This exercise is closed or already reported, so users can't be added."
  if (err?.status === 404) return "Exercise not found. Refresh and try again."
  if (err?.status === 403) return "You don't have permission to add users to this exercise."
  if (err?.serverCode === "INVALID_INPUT") return "Please check the email addresses and try again."
  if (err?.name === "TypeError") return "Network error. Check your connection and try again."
  return "Unable to add users. Try again."
}

export default AddUsersDialog
